'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import AppLogo from '@/components/ui/AppLogo';
import { addDoc, collection, serverTimestamp } from 'firebase/firestore';
import { db } from '@/lib/firestore';
import { ArrowLeft, CheckCircle2, Loader2 } from 'lucide-react';

export default function EarlyAccessPage() {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>(
    'idle'
  );
  const [errorMessage, setErrorMessage] = useState('');

  const isValidEmail = (value: string) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const cleanedEmail = email.trim().toLowerCase();

    if (!isValidEmail(cleanedEmail)) {
      setStatus('error');
      setErrorMessage('Enter a valid email address.');
      return;
    }

    try {
      setStatus('loading');
      setErrorMessage('');

      await addDoc(collection(db, 'earlyAccessSignups'), {
        email: cleanedEmail,
        source: 'early-access-page',
        status: 'waitlist',
        createdAt: serverTimestamp(),
      });

      setStatus('success');
      setEmail('');
    } catch (error) {
      console.error('Early access signup error:', error);
      setStatus('error');
      setErrorMessage('Something went wrong. Please try again.');
    }
  };

  return (
    <main className="min-h-screen bg-[#050505] text-white">
      <div className="pointer-events-none fixed inset-0 bg-[radial-gradient(circle_at_top,rgba(245,200,66,0.16),transparent_34%),radial-gradient(circle_at_bottom_right,rgba(255,255,255,0.07),transparent_30%)]" />

      <div className="relative mx-auto flex min-h-screen max-w-screen-2xl flex-col px-6 py-8 lg:px-10">
        <header className="flex items-center justify-between">
          <Link href="/landing-page" className="flex items-center">
            <AppLogo
              src="/styleai/fhitz-wordmark.png"
              size={34}
              width={132}
              alt="FHITZ logo"
              imageClassName="h-[34px] w-auto object-contain"
            />
          </Link>

          <Link
            href="/landing-page"
            className="inline-flex items-center gap-2 rounded-full border border-white/10 px-4 py-2 text-sm font-medium text-white/70 transition hover:border-[#f5c842]/50 hover:text-[#f5c842]"
          >
            <ArrowLeft size={16} />
            Back
          </Link>
        </header>

        <section className="flex flex-1 items-center justify-center py-20">
          <div className="w-full max-w-xl rounded-[2rem] border border-white/10 bg-white/[0.04] p-7 shadow-[0_24px_80px_rgba(0,0,0,0.45)] backdrop-blur-md sm:p-10">
            <div className="mb-7 inline-flex rounded-full border border-[#f5c842]/25 bg-[#f5c842]/10 px-4 py-2 text-xs font-bold uppercase tracking-[0.28em] text-[#f5c842]">
              Early Access
            </div>

            <h1 className="text-4xl font-black leading-tight tracking-[-0.04em] text-white sm:text-5xl">
              Join the FHITZ waitlist.
            </h1>

            <p className="mt-5 text-base leading-7 text-white/65 sm:text-lg">
              Be one of the first to unlock FHITZ when we go live.
            </p>

            <p className="mt-3 text-sm leading-6 text-white/45">
              Join the waitlist to get early access.
            </p>

            {status === 'success' ? (
              <div className="mt-8 rounded-2xl border border-[#f5c842]/25 bg-[#f5c842]/10 p-5">
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="mt-0.5 text-[#f5c842]" size={22} />

                  <div>
                    <h2 className="font-bold text-white">
                      You’re on the list.
                    </h2>

                    <p className="mt-1 text-sm leading-6 text-white/60">
                      We’ll let you know when FHITZ early access is ready.
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="mt-8 space-y-4">
                <div>
                  <label
                    htmlFor="email"
                    className="mb-2 block text-sm font-semibold text-white/70"
                  >
                    Email address
                  </label>

                  <input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(event) => {
                      setEmail(event.target.value);

                      if (status === 'error') {
                        setStatus('idle');
                        setErrorMessage('');
                      }
                    }}
                    placeholder="you@example.com"
                    className="w-full rounded-2xl border border-white/10 bg-black/35 px-5 py-4 text-base text-white outline-none transition placeholder:text-white/25 focus:border-[#f5c842]/60 focus:ring-4 focus:ring-[#f5c842]/10"
                  />
                </div>

                {status === 'error' && (
                  <p className="text-sm font-medium text-red-400">
                    {errorMessage}
                  </p>
                )}

                <button
                  type="submit"
                  disabled={status === 'loading'}
                  className="flex w-full items-center justify-center gap-2 rounded-2xl bg-[#f5c842] px-6 py-4 text-sm font-black uppercase tracking-[0.16em] text-black shadow-[0_0_30px_rgba(245,200,66,0.28)] transition hover:scale-[1.01] hover:bg-[#ffd84f] disabled:cursor-not-allowed disabled:opacity-70"
                >
                  {status === 'loading' ? (
                    <>
                      <Loader2 size={18} className="animate-spin" />
                      Joining
                    </>
                  ) : (
                    'Get Early Access'
                  )}
                </button>

                <p className="text-center text-xs leading-5 text-white/35">
                  No spam. Just FHITZ launch updates and early access details.
                </p>
              </form>
            )}
          </div>
        </section>
      </div>
    </main>
  );
}